// getEntrancePagUrl.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import {
  PUBLIC_PRODUCT_URL_PATH,
  LIVESTREAM_ENTRANCE_PAG_BASE_FILENAME,
  FEATURE_LIVESTREAM_ENTRANCE_PAG,
} from '../RemoteConfigKeys.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  getVipBenefitLevelThresholdByUserLevel,
  getUserLevelBenefitThresholdList,
} from '../selector/getUserLevelBenefitData.js';

/**
 * Select the entrance PAG animation URL for a livestream user-entered notice.
 * Returns a level-based URL `/{basePrefix}_{tier}.pag`, where tier follows user
 * level decades (vipBenefitLevel - 1, capped at the second-to-last threshold
 * tier; the top tier becomes `_max`).
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {number} level - The entering user's level.
 * @return {string} The PAG file URL, or '' when not applicable.
 * @example
 * getEntrancePagUrl(state, 100);
 * // => 'https://public.swag.live/.../entrance_animation_vip_max.pag'
 */
const getEntrancePagUrl = createCachedSelector(
  state => getRemoteConfigData(state, FEATURE_LIVESTREAM_ENTRANCE_PAG),
  state => getRemoteConfigData(state, PUBLIC_PRODUCT_URL_PATH),
  state => getRemoteConfigData(state, LIVESTREAM_ENTRANCE_PAG_BASE_FILENAME),
  getUserLevelBenefitThresholdList,
  (state, level) =>
    getVipBenefitLevelThresholdByUserLevel(state, level).vipBenefitLevel,
  (state, level) => level,
  (
    isEntrancePagEnabled,
    publicProductUrlPath,
    entrancePagPrefix,
    userLevelBenefitThresholdList,
    vipBenefitLevel,
    level
  ) => {
    if (!isEntrancePagEnabled) return '';
    if (!publicProductUrlPath) return '';
    if (!entrancePagPrefix) return '';

    const isMaxLevel =
      level ===
      (userLevelBenefitThresholdList[userLevelBenefitThresholdList.length - 1]
        ?.max ?? null);
    if (!isMaxLevel && !(vipBenefitLevel >= 1)) return '';

    const maxPagTier = userLevelBenefitThresholdList.length - 2;
    const pagTier = isMaxLevel
      ? 'max'
      : Math.min(vipBenefitLevel - 1, maxPagTier);

    return (
      getResourceUrl({
        path: publicProductUrlPath,
        endpoint: `/${entrancePagPrefix}_${pagTier}.pag`,
        resourceType: PUBLIC,
      })?.href || ''
    );
  }
)((state, level) => `${level}`);

export default getEntrancePagUrl;
